import {
  Alert,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ChipList, EmployeeChip } from "@tngtech/tng-ui";
import { use, useEffect, useMemo, useState } from "react";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { CardsListItem } from "tng-address-collection-shared/types/apiTypes.ts";
import {
  cardStatusesOf,
  duplicatedNamesOf,
  hasErrorStatus,
} from "../columns/cardStatuses.ts";
import { searchFilter } from "../columns/searchFilter.ts";
import { DetailPanelContext } from "../detailPanel/DetailPanelContext.ts";
import { useCardEditorPanel } from "../hooks/useCardEditorPanel.tsx";
import { useCards } from "../hooks/useCards.ts";
import { useInvalidateCardSignaturesCache } from "../hooks/useInvalidateCardSignaturesCache.ts";
import { useReloadCards } from "../hooks/useReloadCards.ts";
import { useSignatureListColumns } from "../hooks/useSignatureListColumns.ts";
import { useUserHasAction } from "../hooks/useUserHasAction.ts";
import { useTranslator } from "../i18n/translator.ts";
import { CardsTable } from "./CardsTable.tsx";
import { CardsTableToolbar } from "./CardsTableToolbar.tsx";
import { CreateCardDialog } from "./CreateCardDialog.tsx";
import { ErrorDisplay } from "./ErrorDisplay.tsx";
import { ManageProjectDialog } from "./ManageProjectDialog.tsx";

const noCards: CardsListItem[] = [];
const noHiddenFields: string[] = [];

export const SignatureList = ({ projectId }: { projectId: string }) => {
  const translator = useTranslator();
  const {
    data,
    refetch: refetchCards,
    isFetching: isFetchingCards,
    isPending: isPendingCards,
    error: cardsError,
  } = useCards(projectId);
  const cards = data?.items ?? noCards;
  const hiddenFields = data?.hiddenFields ?? noHiddenFields;
  const hasPermissions = data?.hasPermissions ?? false;

  const { openPanel, closePanel } = use(DetailPanelContext);

  const invalidateCardSignaturesCache =
    useInvalidateCardSignaturesCache(projectId);
  const reloadCards = useReloadCards(refetchCards);
  const onReload = () => {
    invalidateCardSignaturesCache();
    reloadCards();
  };

  const userHasAction = useUserHasAction(projectId);
  const duplicatedNames = useMemo(() => duplicatedNamesOf(cards), [cards]);
  const rowSelectionFromCards = useMemo(
    () => Object.fromEntries(cards.map((x) => [x.id, x.isSignedByUser])),
    [cards],
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    rowSelectionFromCards,
  );
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);

  const { handleDetailClick, selectedCardId } = useCardEditorPanel({
    cards,
    canDelete: userHasAction(ActionNames.deleteCard),
  });

  useEffect(() => {
    setRowSelection(rowSelectionFromCards);
  }, [rowSelectionFromCards]);

  useEffect(() => {
    if (
      sorting.some(
        ({ id }) => id === ("isSignedByUser" satisfies keyof CardsListItem),
      )
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refetchCards?.();
    }
  }, [refetchCards, sorting]);

  const [globalFilter, setGlobalFilter] = useState<string>("");
  /* eslint-disable-next-line react-hooks/incompatible-library */
  const table = useReactTable<CardsListItem>({
    data: cards,
    getRowId: (row) => row.id,
    columns: useSignatureListColumns(
      handleDetailClick,
      projectId,
      hiddenFields,
      duplicatedNames,
    ),
    getCoreRowModel: getCoreRowModel(),
    globalFilterFn: searchFilter((row) => [
      row.getValue<string>("name"),
      row.original.title,
      row.original.email,
      row.original.companyName,
      row.original.departmentName,
      row.original.senderDisplayName,
      row.original.comment,
    ]),
    state: {
      rowSelection,
      sorting,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    enableRowSelection: true,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  const projectName =
    data?.projectDisplayName && `"${data.projectDisplayName}"`;

  const headerTitle = `${translator("projectCardListTitle")} - ${projectName ?? translator("loading")}`;

  return (
    <Stack padding={2} spacing={2} height="100%">
      <Typography variant="h4">{headerTitle}</Typography>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="h6">
          {translator("projectCardListShepherds")}:
        </Typography>
        <ChipList>
          {data?.projectShepherds.groups.map((employee) => (
            <Chip key={employee.email || employee.name} label={employee.name} />
          ))}
          {data?.projectShepherds.individuals.map((employee) => (
            <EmployeeChip
              key={employee.email || employee.name}
              employee={{ ...employee, email: employee.email || "" }}
            />
          ))}
        </ChipList>
      </Stack>
      {data?.hasPermissions === false ? (
        <Alert severity="error">
          {translator("noPermissionToViewProjectContact")}
        </Alert>
      ) : (
        data?.projectInfoBox && (
          <Alert severity="info">{data.projectInfoBox}</Alert>
        )
      )}
      <CardsTableToolbar
        filterControls={
          <TextField
            placeholder={translator("searchCardsPlaceholder")}
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            size="small"
            variant="outlined"
            sx={{ width: 500 }}
          />
        }
        onReload={onReload}
        isReloading={isFetchingCards}
        pageActions={
          <>
            {userHasAction(ActionNames.createCard) && (
              <Button
                onClick={() =>
                  openPanel(
                    <CreateCardDialog
                      projectId={projectId}
                      onClose={closePanel}
                      cards={cards}
                    />,
                  )
                }
                size="small"
                variant="contained"
                disabled={isFetchingCards}
                sx={{ height: "100%" }}
              >
                {translator("createCardButton")}
              </Button>
            )}
            {userHasAction(ActionNames.manageProject) && (
              <Button
                onClick={() =>
                  openPanel(<ManageProjectDialog onClose={closePanel} />, {
                    attributes: { keepOpenOnProjectNavigation: true },
                  })
                }
                size="small"
                variant="contained"
                disabled={isFetchingCards}
                sx={{ height: "100%" }}
              >
                {translator("manageProjectButton")}
              </Button>
            )}
          </>
        }
      />
      {cardsError ? (
        <ErrorDisplay error={cardsError} />
      ) : isPendingCards ? null : table.getRowModel().rows.length > 0 ? (
        <CardsTable
          table={table}
          isRowSelected={(id) => selectedCardId === id}
          isErrorRow={(row) =>
            hasErrorStatus(cardStatusesOf(row, hiddenFields, duplicatedNames))
          }
          isRowDeemphasized={(card, cellIndex) =>
            !!card.shared && cellIndex >= 2
          }
        />
      ) : (
        hasPermissions && (
          <Typography color="text.secondary">
            {translator(
              cards.length > 0
                ? "projectCardListNoMatchingCards"
                : "projectCardListEmptyList",
            )}
          </Typography>
        )
      )}
    </Stack>
  );
};
